import { useMemo } from 'react'

import {
  DonutChart,
  STAT_GRID_CLASS,
  StatCard,
  StatCardsSkeleton,
  StatLegend,
  StatText,
} from '@/components/stats/StatCard'
import type { ChartConfig } from '@/components/ui/chart'
import type { RecordGroupSummary } from '@/types/api/record-group'

interface RecordGroupStatsProps {
  summary: RecordGroupSummary | undefined
  isLoading?: boolean
  /** Wording for the first level: "root" on the overview, "direct" below a parent group. */
  topLevelLabel?: string
}

// Chart palette remains local to the charts, as the chart component requires explicit series colors.
const DONUT_COLORS = {
  groups: { primary: '#2563eb', secondary: '#bfdbfe' },
  coverage: { primary: '#7c3aed', secondary: '#ddd6fe' },
  hierarchy: { primary: '#059669', secondary: '#a7f3d0' },
  status: { primary: '#10b981', secondary: '#f59e0b' },
} as const

export function RecordGroupStats({ summary, isLoading = false, topLevelLabel = 'root' }: RecordGroupStatsProps) {
  const stats = useMemo(() => {
    const total = summary?.total ?? 0
    const topLevel = summary?.topLevel ?? 0
    const nested = summary?.nested ?? 0
    // Trace-only groups hide their details, so form stats count only openable groups.
    const openable = summary?.openable ?? 0
    const withForm = summary?.withForm ?? 0
    const missingForms = openable - withForm
    const formCoverage = openable > 0 ? Math.round((withForm / openable) * 100) : 0
    const hierarchyPercentage = total > 0 ? Math.round((nested / total) * 100) : 0
    return { total, topLevel, nested, openable, withForm, missingForms, formCoverage, hierarchyPercentage }
  }, [summary])

  const topLabel = topLevelLabel.charAt(0).toUpperCase() + topLevelLabel.slice(1)
  const configs = useMemo(() => ({
    groups: {
      top: { label: topLabel, color: DONUT_COLORS.groups.primary },
      nested: { label: 'Nested', color: DONUT_COLORS.groups.secondary },
    } satisfies ChartConfig,
    form: {
      configured: { label: 'Configured', color: DONUT_COLORS.coverage.primary },
      pending: { label: 'Not Configured', color: DONUT_COLORS.coverage.secondary },
    } satisfies ChartConfig,
    hierarchy: {
      top: { label: topLabel, color: DONUT_COLORS.hierarchy.primary },
      nested: { label: 'Nested', color: DONUT_COLORS.hierarchy.secondary },
    } satisfies ChartConfig,
    status: {
      configured: { label: 'Active', color: DONUT_COLORS.status.primary },
      pending: { label: 'Not Configured', color: DONUT_COLORS.status.secondary },
    } satisfies ChartConfig,
  }), [topLabel])

  if (isLoading || !summary) {
    return <StatCardsSkeleton label="Loading record group overview" />
  }

  return (
    <section aria-label="Record group overview" className={STAT_GRID_CLASS}>
      <StatCard>
        <StatText
          title="Total Groups"
          value={stats.total}
          caption={`${stats.topLevel} ${topLevelLabel} · ${stats.nested} nested`}
        />
        <DonutChart
          data={[
            { segment: 'top', value: stats.topLevel, fill: DONUT_COLORS.groups.primary },
            { segment: 'nested', value: stats.nested, fill: DONUT_COLORS.groups.secondary },
          ]}
          config={configs.groups}
          center={String(stats.total)}
          caption="Groups"
        />
      </StatCard>
      <StatCard>
        <StatText
          title="Form Coverage"
          value={`${stats.formCoverage}%`}
          caption={`${stats.withForm} of ${stats.openable} configured`}
        />
        <DonutChart
          data={[
            { segment: 'configured', value: stats.withForm, fill: DONUT_COLORS.coverage.primary },
            { segment: 'pending', value: stats.missingForms, fill: DONUT_COLORS.coverage.secondary },
          ]}
          config={configs.form}
          center={`${stats.formCoverage}%`}
        />
      </StatCard>
      <StatCard>
        <StatText
          title="Hierarchy"
          value={`${stats.hierarchyPercentage}%`}
          caption={`${stats.nested} nested ${stats.nested === 1 ? 'group' : 'groups'}`}
        />
        <DonutChart
          data={[
            { segment: 'top', value: stats.topLevel, fill: DONUT_COLORS.hierarchy.primary },
            { segment: 'nested', value: stats.nested, fill: DONUT_COLORS.hierarchy.secondary },
          ]}
          config={configs.hierarchy}
          center={`${stats.hierarchyPercentage}%`}
        />
      </StatCard>
      <StatCard>
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
          <StatLegend
            items={[
              { label: 'Active', value: stats.withForm, color: DONUT_COLORS.status.primary },
              { label: 'Not Configured', value: stats.missingForms, color: DONUT_COLORS.status.secondary },
            ]}
          />
        </div>
        <DonutChart
          data={[
            { segment: 'configured', value: stats.withForm, fill: DONUT_COLORS.status.primary },
            { segment: 'pending', value: stats.missingForms, fill: DONUT_COLORS.status.secondary },
          ]}
          config={configs.status}
          center={String(stats.openable)}
          caption="Groups"
        />
      </StatCard>
    </section>
  )
}
